import {
  CardType,
  PaymentMethod,
} from "store/paymentMethods/paymentMethods-state";
import {
  AccountTypeConfiguration,
  FeeConfiguration,
  PaymentMethodConfiguration,
} from "store/config";
import { UserAccount } from "store/user/user";
import { ProblemDetails } from "./errors";
import { format, isBefore, isValid, parse } from "date-fns";
import { EWalletConfiguration } from "store/config/ewallet-configuration";

export const getEnumByValue = <T extends Record<string, number | string>>(
  enumObj: T,
  value: T[keyof T],
): keyof T | null => {
  const keys = Object.keys(enumObj) as Array<keyof T>;
  for (const key of keys) {
    if (enumObj[key] === value) {
      return key;
    }
  }
  return null;
};

export const renderConditionalProp = (condition: boolean, prop: string) => {
  const props: any = {};
  if (condition) props[prop] = true;
  return props;
};

export enum PAYMENT_METHOD_TYPE {
  ach = 1,
  card = 2,
  eWallet = 3,
  lockbox = 4,
}

export enum EWALLET_TYPE {
  GooglePay = 1,
  ApplePay = 2,
}

export type PaymentMethodType = keyof typeof PAYMENT_METHOD_TYPE;

export enum PAYMENT_TYPE {
  S = "Standard",
  P = "Principal",
  E = "Escrow",
}

export type PaymentType = keyof typeof PAYMENT_TYPE;

const paymentMethodTypeMap: { [key: string]: number } = {
  ach: 1,
  card: 2,
  eWallet: 3,
  lockbox: 4,
};

const paymentAccountTypeMap: { [key: string]: number } = {
  checking: 1,
  savings: 2,
};

const amountTypeMap: { [key: string]: number } = {
  userEntered: 1,
  amountDue: 2,
  minimum: 3,
  total_balcnce: 4,
};

const paymentScheduleMap: { [key: string]: number } = {
  userProvided: 1,
  dueDate: 2,
};

const PaymentStatusTypeMap: { [key: string]: number } = {
  Canceled: 1,
  Paid: 2,
  Pending: 3,
  Returned: 4,
  Scheduled: 5,
  "Failed Payment": 6,
  Refunded: 9,
  "Insufficient Funds": 10,
};

const ScheduleStatusTypeMap: { [key: string]: number } = {
  Canceled: 1,
  Skipped: 2,
  Scheduled: 0,
  Processed: 30,
};

const ScheduleSortColumnTypeMap: { [key: string]: number } = {
  scheduledPaymentDate: 1,
  paymentAmount: 2,
  status: 3,
  created: 4,
};

const SortColumnTypeMap: { [key: string]: number } = {
  paymentDate: 1,
  confirmationNumber: 2,
  amountPaid: 3,
  amountDue: 4,
  paymentMethodName: 5,
  status: 6,
};

const SortDirTypeMap: { [key: string]: number } = {
  asc: 1,
  desc: 2,
};

const TimeZoneIdMap: { [key: string]: number } = {
  Eastern: 0,
  Central: 1,
  Mountain: 2,
  Pacific: 3,
};

const getKey = (obj: any, val: any) =>
  Object.keys(obj).find((key) => obj[key] === val);

export const typeMapper = (val: string | number, type: string) => {
  let returnVal;
  switch (type) {
    case "account":
      returnVal = paymentAccountTypeMap[val];
      break;
    case "PaymentStatus":
      returnVal = PaymentStatusTypeMap[val];
      break;
    case "ScheduleStatus":
      returnVal = ScheduleStatusTypeMap[val];
      break;
    case "ScheduleSortColumn":
      returnVal = ScheduleSortColumnTypeMap[val];
      break;
    case "SortColumn":
      returnVal = SortColumnTypeMap[val];
      break;
    case "SortDir":
      returnVal = SortDirTypeMap[val];
      break;
    case "amountType":
      returnVal = amountTypeMap[val];
      break;
    case "amountTypeReverse":
      returnVal = getKey(amountTypeMap, val);
      break;
    case "paymentSchedule":
      returnVal = paymentScheduleMap[val];
      break;
    case "paymentScheduleReverse":
      returnVal = getKey(paymentScheduleMap, val);
      break;
    case "accountReverse":
      returnVal = getKey(paymentAccountTypeMap, val);
      break;
    case "paymentMethodType":
      returnVal = paymentMethodTypeMap[val];
      break;
    case "paymentMethodTypeReverse":
      returnVal = getKey(paymentMethodTypeMap, val);
      break;
    case "ScheduleSortColumnReverse":
      returnVal = getKey(ScheduleSortColumnTypeMap, val);
      break;
    case "SortColumnReverse":
      returnVal = getKey(SortColumnTypeMap, val);
      break;
    case "SortDirReverse":
      returnVal = getKey(SortDirTypeMap, val);
      break;
    case "PaymentStatusReverse":
      returnVal = getKey(PaymentStatusTypeMap, val);
      break;
    case "ScheduleStatusReverse":
      returnVal = getKey(ScheduleStatusTypeMap, val);
      break;
    case "TimeZoneId":
      returnVal = TimeZoneIdMap[val];
      break;
    case "TimeZoneIdReverse":
      returnVal = getKey(TimeZoneIdMap, val);
      break;
  }
  return returnVal;
};

export const getCardName = (cardType: CardType) => {
  if (cardType === CardType.VISA) {
    return "Visa";
  } else if (cardType === CardType.MASTER_CARD) {
    return "Master";
  } else if (cardType === CardType.AMEX) {
    return "Amex";
  } else if (cardType === CardType.DISCOVER) {
    return "Discover";
  }
  return "JCB";
};

export const getWalletName = (walletType: number) => {
  switch (walletType) {
    case EWALLET_TYPE.GooglePay:
      return "Google Pay";
    case EWALLET_TYPE.ApplePay:
      return "Apple Pay";
    default:
      return "eWallet";
  }
};

export const getCardTypeFromTsysData = (cardType: string) => {
  switch (cardType) {
    case "V":
      return CardType.VISA;
    case "M":
      return CardType.MASTER_CARD;
    case "X":
      return CardType.AMEX;
    case "R":
      return CardType.DISCOVER;
    default:
      return CardType.JCB;
  }
};
export const getAddress = (user: UserAccount) => {
  return "".concat(
    user?.addressLine1!,
    " ",
    user?.addressLine2!,
    " ",
    user?.city!,
    " ",
    user?.state!,
  );
};

/*
 * mask the whole string except last length provided.
 * @param {string} val
 * @param {string} count - last unmasked digit
 * @return {string}
 */

export const maskCardString = (val: string, count: number = 4) => {
  const showDigit = getLastDigit(val, count);
  const finalVal = `xxxx xxxx xxxx ${showDigit}`;
  return finalVal;
};

export const maskString = (val: string, count: number = 4) => {
  const showDigit = getLastDigit(val, count);
  const finalVal = showDigit.padStart(val.length, "x");
  return finalVal;
};

export const getLastDigit = (val: string, count: number = 4) => {
  return val.slice(-count);
};

export const extractError = (
  error: ProblemDetails | any,
  defaultVal: string = "something_went_wrong",
) => {
  let errors: string[] = [defaultVal];
  if (error.response && error.response.data && error.response.data.errors) {
    const errObjects: any = Object.values(error.response.data.errors);
    errors = [].concat(...errObjects);
    if (errors.length > 0) {
      return errors;
    } else if (
      error.response &&
      error.response.data &&
      error.response.data.detail
    ) {
      return [error.response.data.detail];
    } else if (
      error.response &&
      error.response.data &&
      error.response.data.title
    ) {
      return [error.response.data.title];
    }
  } else if (error.errors) {
    const problemDetails = error as ProblemDetails;
    const errObjects: any = Object.values(problemDetails.errors);
    errors = [].concat(...errObjects);
    if (errors.length > 0) {
      return errors;
    }
  }
  return errors;
};

/*
 * String conversion to Initil letter to upper case on whole string
 * @param {string} str
 * @return {string}
 */

export const toInitUpperCase = function (str: string) {
  return str
    .replace(/\s(.)/g, function ($1) {
      return $1.toUpperCase();
    })
    .replace(/\s/g, " ")
    .replace(/^(.)/, function ($1) {
      return $1.toUpperCase();
    });
};

/*
 * parse data from expession - ex. "0 0 1 * *",
 * @param {string} expression
 * @return {string} 2nd value , 1 - in the expression example.
 */

export const getDateFromExpression = (expression: string) => {
  const res = expression.split(" ");
  return res[2];
};

/*
 * String conversion to Camel Case
 * @param {string} str
 * @return {string} in Camel Case
 */

export const toCamelCase = (str: string) => {
  return str
    .replace(/\s(.)/g, function ($1) {
      return $1.toUpperCase();
    })
    .replace(/\s/g, "")
    .replace(/^(.)/, function ($1) {
      return $1.toLowerCase();
    });
};

/*
 * Fetch data from config based on type
 * @param {AccountTypeConfiguration} config
 * @param {string} type
 * @return {any} based on logic and requirnment
 */

export const fetchFromConfig = (
  config: AccountTypeConfiguration,
  type: string,
) => {
  if (type === "initialPaymentTypes")
    // This now returns the first ordered paymentType obj code instead of a key (44393)
    return config.paymentTypes.find((pt) => pt.ordinalPosition === 0)?.code;
};

/*
 * Check if the amount is in range
 * @param {PaymentMethodConfiguration} currentPaymentConfig
 * @param {number | undefined} amountDue
 * @return {boolean}
 */

export const checkLoanInRange = (
  amountDue: number | undefined,
  currentPaymentConfig: PaymentMethodConfiguration,
) => {
  const minLoanAmount = currentPaymentConfig?.payments?.minimumPaymentAmount;
  const maxLoanAmount = currentPaymentConfig?.payments?.maximumPaymentAmount;

  return minLoanAmount && maxLoanAmount
    ? !!amountDue && amountDue >= minLoanAmount && amountDue <= maxLoanAmount
    : false;
};

export const determineFeeConfiguration = (
  paymentMethod: PaymentMethod | undefined,
  currentPaymentConfig: PaymentMethodConfiguration | null,
  isRecurring: boolean,
  defaultFee: number,
): FeeConfiguration | null => {
  if (paymentMethod === undefined) {
    return null;
  }

  if (!currentPaymentConfig?.fees) {
    return null;
  }
  const { fees } = currentPaymentConfig;
  const { cardBinType } = paymentMethod;
  if (isRecurring && cardBinType !== undefined) {
    const recurringCardFee = Object.values(fees).find(
      (fee: FeeConfiguration) =>
        fee.isRecurring &&
        fee.typeCode !== undefined &&
        fee.typeCode === cardBinType,
    );
    if (recurringCardFee) {
      return recurringCardFee;
    }
  }

  if (isRecurring) {
    const recurringFee = Object.values(fees).find(
      (fee: FeeConfiguration) =>
        fee.isRecurring &&
        (fee.typeCode === null || fee.typeCode === undefined),
    );
    if (recurringFee) {
      return recurringFee;
    }
  }

  if (cardBinType !== undefined) {
    const nonRecurringCardFee = Object.values(fees).find(
      (fee: FeeConfiguration) =>
        !fee.isRecurring &&
        fee.typeCode !== undefined &&
        fee.typeCode === cardBinType,
    );
    if (nonRecurringCardFee) {
      return nonRecurringCardFee;
    }
  }

  const genericNonRecurringFee = Object.values(fees).find(
    (fee: FeeConfiguration) =>
      !fee.isRecurring && (fee.typeCode === null || fee.typeCode === undefined),
  );
  if (genericNonRecurringFee) {
    return genericNonRecurringFee;
  }

  if (!isRecurring && fees.ConvenienceFee) {
    return fees.ConvenienceFee;
  }

  if (!isRecurring && defaultFee) {
    return {
      name: "Default Fee",
      amount: defaultFee,
      isRecurring: false,
      isPercentage: false,
    };
  }

  return {
    name: "No Fee",
    amount: 0,
    isPercentage: false,
    isRecurring: isRecurring,
  };
};

export const calculateFee = (
  convenienceFee?: FeeConfiguration | null,
  defaultFee?: number,
  amount?: number,
): number => {
  let fee = 0;
  if (convenienceFee && convenienceFee?.amount) {
    if (convenienceFee.isPercentage) {
      // Handle if the config value is not in decimal format,
      // otherwise normal percentage fee calculation
      if (convenienceFee.amount >= 1) {
        fee = amount ? +amount * (convenienceFee.amount / 100) : 0;
      } else {
        fee = amount ? +amount * convenienceFee.amount : 0;
      }
    } else {
      fee = convenienceFee.amount;
    }
  } else {
    fee = defaultFee ? defaultFee : 0;
  }

  return parseFloat(fee.toFixed(2));
};

export const getSearchParamValue = (
  paramKey: string,
  params: URLSearchParams,
) => {
  const searchKey = Array.from(params.keys()).find(
    (key) => key.toLowerCase() === paramKey.toLowerCase(),
  );
  if (searchKey !== undefined) {
    return params.get(searchKey);
  }
  return null;
};

export const isExpired = (
  paymentMethodExpiration: string | undefined,
  beforeDate?: string,
) => {
  if (!paymentMethodExpiration) return false;

  let compareDate = new Date();
  if (beforeDate != null) {
    compareDate = new Date(beforeDate);
  }
  const expirationDate = parseExpirationDate(paymentMethodExpiration);
  if (expirationDate != null) {
    return isBefore(expirationDate, compareDate);
  }
  return false;
};

export const parseExpirationDate = (expirationDate: string): Date | null => {
  if (!expirationDate) {
    return null;
  }
  const [month, year] = expirationDate.split("/");

  if (!month || !year || isNaN(Number(month)) || isNaN(Number(year))) {
    return null;
  }

  const lastDayOfMonth = new Date(Number(`20${year}`), Number(month), 0);
  lastDayOfMonth.setHours(23, 59, 59, 999);
  return lastDayOfMonth;
};

export const convert24to12HourTime = (timeString: string) => {
  if (!timeString) {
    return "";
  }

  const parsed = parse(timeString, "HH:mm:ss", new Date());

  if (!isValid(parsed)) {
    return "";
  }
  return format(parsed, "h:mm a");
};

export const determinePaymentMethodDisplayText = (
  paymentMethodType: number,
  walletType?: number | null,
) => {
  switch (paymentMethodType) {
    case PAYMENT_METHOD_TYPE.ach:
      return "ACH";
    case PAYMENT_METHOD_TYPE.card:
      return "Card";
    case PAYMENT_METHOD_TYPE.eWallet:
      if (walletType) return getWalletName(walletType);
      return "eWallet";
    default:
      return "";
  }
};

export function getEffectiveEWalletConfiguration(
  config: EWalletConfiguration,
  accountType?: string,
): EWalletConfiguration {
  if (!accountType || !config.accountTypes) {
    return config;
  }

  const accountTypeConfig = config.accountTypes[accountType];
  if (!accountTypeConfig) {
    return config;
  }

  const effectiveAccountTypeConfig: EWalletConfiguration = {
    clientId: config.clientId,
    idPId: config.idPId,
    customerKey: config.customerKey,
    paymentGateway: accountTypeConfig.paymentGateway!,
    debitCardOnly: accountTypeConfig.debitCardOnly,
    slimCDConfiguration: accountTypeConfig.slimCDConfiguration,
    authorizeNetConfiguration: accountTypeConfig.authorizeNetConfiguration,
    applePayConfiguration: config.applePayConfiguration,
    accountTypes: config.accountTypes,
  };
  return effectiveAccountTypeConfig;
}
